import React, { useState, useEffect, useRef } from 'react';
import AccessConfigEditor from './AccessConfigEditor';
import {
  resolveAccessConfig, normalizeAccessConfig, ROLE_SUGGESTED_CONFIG,
} from '../../utils/stakeholderAccess';
import { listAccessTemplates, getAccessTemplateConfig } from '../../services/projectApi';
import './StakeholderAccessCell.css';

// Bir paydaşın ERIŞIM sütunu — şablon dropdown'u + "Ozellestir" popover'ı.
// value: { templateId, override } — null/undefined olabilir.
// onChange({ templateId, override })
const StakeholderAccessCell = ({ stakeholderKey, value, onChange }) => {
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [tplConfigCache, setTplConfigCache] = useState({});
  const popRef = useRef(null);
  const triggerRef = useRef(null);

  const templateId = value?.templateId || '';
  const override = value?.override || null;

  // Şablonları yükle
  useEffect(() => {
    let cancelled = false;
    listAccessTemplates()
      .then(list => { if (!cancelled) setTemplates(Array.isArray(list) ? list : []); })
      .catch(() => { /* sessiz — backend bağlanmadıysa boş liste */ });
    return () => { cancelled = true; };
  }, []);

  // Popover dışı tıklayınca kapat
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)
          && triggerRef.current && !triggerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Şablon config'ini çek (cache'le)
  const ensureTplConfig = async (id) => {
    if (!id) return null;
    if (tplConfigCache[id]) return tplConfigCache[id];
    try {
      const cfg = await getAccessTemplateConfig(id);
      setTplConfigCache(prev => ({ ...prev, [id]: cfg }));
      return cfg;
    } catch { return null; }
  };

  const handleTemplateChange = async (id) => {
    if (id === '__custom__') {
      // Mevcut çözümlü config'i override olarak kopyala — popover açılır
      const tplCfg = templateId ? await ensureTplConfig(templateId) : null;
      const resolved = resolveAccessConfig(stakeholderKey, override, tplCfg);
      onChange?.({ templateId: '', override: normalizeAccessConfig(resolved) });
      setOpen(true);
    } else {
      onChange?.({ templateId: id, override: null });
    }
  };

  // Popover içindeki AccessConfigEditor için göstereceğimiz config
  const popoverConfig = override
    || resolveAccessConfig(stakeholderKey, null, tplConfigCache[templateId])
    || ROLE_SUGGESTED_CONFIG[stakeholderKey];

  const handlePopoverChange = (cfg) => {
    onChange?.({ templateId: '', override: cfg });
  };

  const handleResetToDefault = () => {
    onChange?.({ templateId: '', override: null });
    setOpen(false);
  };

  const dropdownValue = override ? '__custom__' : templateId;

  return (
    <div className="sac-wrap">
      <div className="sac-row">
        <select
          ref={triggerRef}
          className="sac-select"
          value={dropdownValue}
          onChange={e => handleTemplateChange(e.target.value)}
        >
          <option value="">Default ({stakeholderRoleLabel(stakeholderKey)})</option>
          {templates.map(t => (
            <option key={t.id} value={t.id}>{t.title || t.name || 'Sablon'}</option>
          ))}
          {override && <option value="__custom__">Özel</option>}
        </select>
        <button
          type="button"
          className="sac-customize-btn"
          onClick={() => setOpen(o => !o)}
          title="Erişim ayarlarını özelleştir"
        >
          {open ? '▲' : 'Özelleştir'}
        </button>
      </div>

      {open && (
        <div className="sac-popover" ref={popRef}>
          <div className="sac-popover-header">
            <span className="sac-popover-title">Erişim Ayarları</span>
            <button type="button" className="sac-link-btn" onClick={handleResetToDefault}>
              Default'a don
            </button>
          </div>
          <AccessConfigEditor value={popoverConfig} onChange={handlePopoverChange} dense />
        </div>
      )}
    </div>
  );
};

function stakeholderRoleLabel(key) {
  // Default rol bazlı başlık
  const map = {
    malSahibi: 'Mal Sahibi',
    muteahhit: 'Muteahhit',
    mimariMuellif: 'Mimari Muellif',
    elektrikMuellif: 'Elektrik Muellif',
    mekanikMuellif: 'Mekanik Muellif',
    kontrolMuhendis: 'Kontrol Muhendisi',
    yapiDenetimId: 'Yapi Denetim',
    elektrikDagitimId: 'EDF',
    telekomId: 'Telekom',
    itfaiyeId: 'Itfaiye',
  };
  return map[key] || 'Default';
}

export default StakeholderAccessCell;
